import React, { useEffect, useState } from "react";
import Sidebar from "../side_bar/SidebarAdmin";
import avatar from "../../../assets/avatar.png";
import { useAuth } from "../../../context/AuthContext";
import {
  getAdminProfile,
  updateAdminProfile,
} from "../../../api/admin/profile";
import Header from "../../../components/common/Header";
import { AccountPageProps, accountRoleLabel } from "../../../components/common/accountConfig";

interface OtherInfoForm {
  instituicao: string;
  curso: string;
  nivel_academico: string;
  link_lattes: string;
}

const placeholders: OtherInfoForm = {
  instituicao: "Insira a instituição que faz parte",
  curso: "Ex.: Bacharelado em Engenharia da Computação",
  nivel_academico: "Grau Acadêmico (Ex.: Bacharelado, Mestrado)",
  link_lattes: "Link para Currículo Lattes",
};
const emptyInfo: OtherInfoForm = { instituicao: "", curso: "", nivel_academico: "", link_lattes: "" };

const OtherInformationAdmin: React.FC<AccountPageProps> = ({ role = "Admin" }) => {
  useEffect(() => {
    document.title = "Outras informações - Insecta";
  }, []);
  const { getUserIdFromToken, getCsrfToken, user } = useAuth();
  const userId = getUserIdFromToken();
  const [formData, setFormData] = useState<OtherInfoForm>(emptyInfo);
  const [savedData, setSavedData] = useState<OtherInfoForm>(emptyInfo);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const clearMessages = () =>
    setTimeout(() => {
      setError("");
      setSuccess("");
    }, 5000);

  useEffect(() => {
    const load = async () => {
      try {
        if (!userId) {
          setError("Usuário não autenticado.");
          return;
        }
        const token = getCsrfToken();
        if (!token) {
          setError("Token não encontrado.");
          return;
        }
        const data = await getAdminProfile(userId, token);
        const loaded = {
          instituicao: data.instituicao || "",
          curso: data.curso || "",
          nivel_academico: data.nivel_academico || "",
          link_lattes: data.link_lattes || "",
        };
        setFormData(loaded);
        setSavedData(loaded);
      } catch (e: any) {
        console.error(e);
        setError("Erro ao carregar dados.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId, getCsrfToken]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (saving) return;
    try {
      setSaving(true);
      setError("");
      setSuccess("");
      if (!userId) {
        setError("Usuário não autenticado.");
        return;
      }
      const token = getCsrfToken();
      if (!token) {
        setError("Token não encontrado.");
        return;
      }
      if (
        !formData.instituicao.trim() ||
        !formData.curso.trim() ||
        !formData.nivel_academico.trim()
      ) {
        setError("Preencha os campos obrigatórios.");
        return;
      }
      const payload: any = { ...formData };
      const updated = await updateAdminProfile(userId, payload, token);
      const confirmed = {
        instituicao: updated.instituicao || formData.instituicao,
        curso: updated.curso || formData.curso,
        nivel_academico: updated.nivel_academico || formData.nivel_academico,
        link_lattes: updated.link_lattes || formData.link_lattes,
      };
      setFormData(confirmed);
      setSavedData(confirmed);
      setSuccess("Informações atualizadas com sucesso!");
      clearMessages();
    } catch (e: any) {
      console.error(e);
      setError("Erro ao atualizar.");
      clearMessages();
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setError("");
    setSuccess("");
    setFormData(savedData);
  };

  if (loading)
    return (
      <div className="container-fluid text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );

  return (
    <div className="account-profile-page">
      <Header />
      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          ></button>
        </div>
      )}
      {success && (
        <div
          className="alert alert-success alert-dismissible fade show"
          role="alert"
        >
          {success}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccess("")}
          ></button>
        </div>
      )}
      <div className="account-profile-layout">
        <div className="account-navigation">
          <Sidebar
            role={role}
            activeItem="outras-informacoes"
            avatar={avatar}
            name={user?.nome_completo || "Administrador"}
          />
        </div>
        <main className="account-profile-content">
          <div className="account-page-heading">
            <h1>Outras informações</h1>
          </div>
          {saving && (
            <div
              className="alert alert-info d-flex align-items-center"
              role="alert"
            >
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></div>
              Salvando...
            </div>
          )}
          <div className="account-panel">
            <div className="account-panel-heading"><h2>Informações acadêmicas</h2></div>
            <form
              aria-label="Informações acadêmicas"
              onSubmit={(e) => {
                e.preventDefault();
                handleSave();
              }}
            >
              <fieldset className="account-fields" disabled={saving}>
              <div className="account-field account-field-wide">
                <label htmlFor="other-profile-type" className="form-label">
                  Tipo de perfil
                </label>
                <input
                  id="other-profile-type"
                  type="text"
                  className="form-control"
                  value={accountRoleLabel(role)}
                  readOnly
                />
              </div>
              <div className="account-field">
                <label htmlFor="other-institution" className="form-label">
                  Instituição
                </label>
                <input
                  id="other-institution"
                  required
                  type="text"
                  name="instituicao"
                  value={formData.instituicao}
                  onChange={handleChange}
                  className="form-control"
                  placeholder={placeholders.instituicao}
                />
              </div>
                <div className="account-field">
                  <label htmlFor="other-course" className="form-label">
                    Curso ou Área de Formação
                  </label>
                  <input
                    id="other-course"
                    required
                    type="text"
                    name="curso"
                    value={formData.curso}
                    onChange={handleChange}
                    className="form-control"
                    placeholder={placeholders.curso}
                  />
                </div>
                <div className="account-field">
                  <label htmlFor="other-level" className="form-label">
                    Nível de Formação Acadêmica
                  </label>
                  <input
                    id="other-level"
                    required
                    type="text"
                    name="nivel_academico"
                    value={formData.nivel_academico}
                    onChange={handleChange}
                    className="form-control"
                    placeholder={placeholders.nivel_academico}
                  />
                </div>
              <div className="account-field">
                <label htmlFor="other-lattes" className="form-label">
                  Link do currículo Lattes
                </label>
                <input
                  id="other-lattes"
                  type="url"
                  name="link_lattes"
                  value={formData.link_lattes}
                  onChange={handleChange}
                  className="form-control"
                  placeholder={placeholders.link_lattes}
                />
              </div>
              </fieldset>
              <div className="account-form-actions">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-outline-secondary"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Salvando…" : "Salvar alterações"}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default OtherInformationAdmin;
